import { Button, OTP_VALIDITY_SECONDS, RESEND_COOLDOWN_SECONDS } from '@cibli/shared';
import { useTranslation } from 'react-i18next';

import { OtpInput, useOTPVerification, useTimer } from '@/features/auth/otp';

interface OTPFormProps {
  identifier: string;
  isEmail: boolean;
  hasExistingOTP?: boolean;
  timeLeft?: number;
  onResend: () => void;
  onBack: () => void;
  onSuccess?: () => void;
}

export default function OTPForm({
  identifier,
  isEmail,
  hasExistingOTP = false,
  timeLeft: initialTimeLeft,
  onResend,
  onBack,
  onSuccess,
}: OTPFormProps) {
  const { t } = useTranslation();
  const timer = useTimer({ initialSeconds: initialTimeLeft || OTP_VALIDITY_SECONDS });

  const verification = useOTPVerification({
    identifier,
    isEmail,
    onResend: async () => {
      await onResend();
      timer.reset(OTP_VALIDITY_SECONDS);
    },
    onSuccess,
  });

  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-col gap-3">
        <h1 className="text-3xl md:text-4xl font-semibold">{t('dashboard.auth.otp.title')}</h1>
        <p className="text-lg text-muted-foreground">
          {hasExistingOTP ? (
            <>
              {t('dashboard.auth.otp.alreadySent', {
                method: isEmail ? t('dashboard.auth.otp.byEmail') : t('dashboard.auth.otp.bySMS'),
              })}{' '}
              <span className="font-medium">{identifier}</span>
              <br />
              <span className="text-primary">{t('dashboard.auth.otp.existingCodeNotice')}</span>
            </>
          ) : (
            <>
              {t('dashboard.auth.otp.sent', {
                method: isEmail ? t('dashboard.auth.otp.byEmail') : t('dashboard.auth.otp.bySMS'),
              })}{' '}
              <span className="font-medium">{identifier}</span>
            </>
          )}
        </p>
      </div>

      <form
        onSubmit={e => {
          e.preventDefault(); // Prevent form submission
          if (verification.code.replace(/\D/g, '').length === 6 && !verification.isSubmitting) {
            verification.handleManualSubmit();
          }
        }}
        className="flex flex-col gap-6"
      >
        <OtpInput
          value={verification.code}
          onChange={verification.handleChange}
          onComplete={verification.handleManualSubmit}
          disabled={verification.isSubmitting}
          small
        />

        {verification.error && (
          <p className="text-base text-destructive text-center">{verification.error}</p>
        )}

        <div className="flex flex-col gap-2 text-base text-muted-foreground text-center">
          <p>
            {t('dashboard.auth.otp.validFor')}{' '}
            <span className="font-medium">{timer.formatTime(timer.timeLeft)}</span>
          </p>
          {timer.isExpired ? (
            <p className="text-destructive">{t('dashboard.auth.otp.expired')}</p>
          ) : null}
        </div>

        <Button
          onClick={verification.handleManualSubmit}
          className="w-full h-12 text-base"
          size="lg"
          disabled={verification.isSubmitting || verification.code.replace(/\D/g, '').length !== 6}
        >
          {verification.isSubmitting
            ? t('dashboard.auth.otp.submittingButton')
            : t('dashboard.auth.otp.submitButton')}
        </Button>

        <div className="flex justify-between text-base">
          <Button
            type="button"
            variant="link"
            onClick={onBack}
            className="h-auto p-0 text-muted-foreground"
            disabled={verification.isSubmitting}
          >
            {t('dashboard.auth.otp.backButton')}
          </Button>
          <Button
            type="button"
            variant="link"
            onClick={verification.handleResend}
            className="h-auto p-0"
            disabled={
              verification.isSubmitting ||
              timer.timeLeft > OTP_VALIDITY_SECONDS - RESEND_COOLDOWN_SECONDS
            }
            title={
              timer.timeLeft > OTP_VALIDITY_SECONDS - RESEND_COOLDOWN_SECONDS
                ? t('dashboard.auth.otp.waitTooltip', {
                    seconds: RESEND_COOLDOWN_SECONDS - (OTP_VALIDITY_SECONDS - timer.timeLeft),
                  })
                : ''
            }
          >
            {t('dashboard.auth.otp.resendButton')}
            {timer.timeLeft > OTP_VALIDITY_SECONDS - RESEND_COOLDOWN_SECONDS &&
              ` (${RESEND_COOLDOWN_SECONDS - (OTP_VALIDITY_SECONDS - timer.timeLeft)}s)`}
          </Button>
        </div>
      </form>
    </div>
  );
}
